import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { CameraAlt, Settings } from '@mui/icons-material';
import { theme } from './theme';
import { MainStoreContextItem } from './Stores/MainStore';
import { FaceSdkStoreContextItem } from './Stores/FaceSdkStore';
import { Setup } from './Setup';
import { observer } from 'mobx-lite';
import { VideoDisplayPlaceHolder } from './Components/Cam/VideoPlaceHolder';
import { VideoDisplay } from './Components/Cam/VideoDisplay';
import { StatusIndicator } from './Components/StatusIndicator';
import { AuthenticationDialog } from './Components/AuthenticationDialog';
import { ProtectedRoute } from './Components/ProtectedRoute';
import { LoadingFeedback } from './Components/LoadingFeedback';
import DebugLogPanel from './Components/DebugLogPanel';
import { GuardScreen } from './Components/Guard/GuardScreen';
import { useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAppVersionCheck } from './hooks/useAppVersionCheck';

const CamArea: React.FC = observer(() => {
    const store = MainStoreContextItem.useStore();

    const { permissionGranted, stream } = store.mediaDeviceStore;

    // Ensure we get the media stream when the component mounts (similar to sample project)
    useEffect(() => {
        // First get device list and permissions, which will auto-select first device and start stream
        store.mediaDeviceStore.getMediaDevices();
    }, [store.mediaDeviceStore]);

    const isGuard = store.settingsStore.appMode === 'guard';
    if (isGuard) return <GuardScreen />;

    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
                height: '100%',
                width: '100%',
                position: 'relative',
                minHeight: 'calc(100vh - 56px)',
            }}
        >
            {/* The MediaDeviceStore handles getting camera permissions and stream on initialization.
                This component just needs to react to the state. */}
            {permissionGranted && stream ? <VideoDisplay stream={stream} /> : <VideoDisplayPlaceHolder />}
            <StatusIndicator />
        </Stack>
    );
});

const AppShellContent: React.FC = observer(() => {
    const store = MainStoreContextItem.useStore();
    const navigate = useNavigate();
    const location = useLocation();

    // Check if we're on the camera/home route
    const isOnCameraRoute = location.pathname === '/';

    // Pause face detection when auth dialog is open or not on camera route
    const authDialogOpen = store.showAuthDialog.get();
    useEffect(() => {
        const { faceServiceStore } = store;
        if (authDialogOpen || !isOnCameraRoute) {
            faceServiceStore.stopFaceDetection();
        } else {
            // On camera route with no dialog — let VideoDisplay handle auto-start,
            // but also try here with a delay as a fallback
            const timer = setTimeout(() => {
                faceServiceStore.autoStartDetection();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [authDialogOpen, store, isOnCameraRoute]);

    // Toggle between home and setup routes
    const handleMenuToggle = useCallback(() => {
        if (isOnCameraRoute) {
            // Going to setup — require authentication
            if (!store.isAuthenticated.get()) {
                store.requestAuthentication();
                const checkAuth = () => {
                    if (store.isAuthenticated.get()) {
                        navigate('/setup');
                        return;
                    }
                    setTimeout(checkAuth, 100);
                };
                checkAuth();
            } else {
                navigate('/setup');
            }
        } else {
            // Going home — deauthenticate
            store.deAuthenticate();
            navigate('/');
        }
    }, [isOnCameraRoute, store, navigate]);

    // Calculate header opacity based on route
    const getHeaderOpacity = () => {
        return isOnCameraRoute ? 0 : 1;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <motion.div
                initial={{ opacity: getHeaderOpacity() }}
                animate={{ opacity: getHeaderOpacity() }}
                transition={{
                    duration: 0.3,
                    ease: 'easeInOut',
                }}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1100,
                    height: 56,
                }}
            >
                <AppBar position="static" sx={{ height: 56, backgroundColor: 'rgba(18, 18, 18, 0.45)', backdropFilter: 'blur(16px)', backgroundImage: 'none' }}>
                    <Toolbar variant="dense" sx={{ minHeight: 56, px: 0.5, py: 0 }}>
                        {isOnCameraRoute ? (
                            <IconButton
                                edge="start"
                                color='primary'
                                onClick={handleMenuToggle}
                                size="medium"
                                sx={{ p: 0.5 }}
                            >
                                <Settings sx={{ fontSize: 48 }} />
                            </IconButton>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CameraAlt />}
                                onClick={handleMenuToggle}
                            >
                                Return to App
                            </Button>
                        )}
                    </Toolbar>
                </AppBar>
            </motion.div>
            <Box component="main" sx={{ flexGrow: 1, p: 0 }}>
                <Routes>
                    <Route
                        path="/setup/*"
                        element={
                            <ProtectedRoute>
                                <Box sx={{ mt: '56px', mx: '15px', mb: '15px' }}>
                                    <Setup />
                                </Box>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="*"
                        element={
                            <ProtectedRoute>
                                <CamArea />
                            </ProtectedRoute>
                        }
                    />
                </Routes>
                <AuthenticationDialog />
                <LoadingFeedback />
                <DebugLogPanel />
            </Box>
        </Box>
    );
});


// DO NOT REMOVE: Used by Fully Kiosk Browser for logging broadcasts to console
// Attached to window so Fully Kiosk can call it via eval
(window as any).logBroadcast = function(action: any, extras: any) {
  console.log(`Fully Broadcast: ${action} - ${extras}`);
};

const AppImpl: React.FC = () => {
    useAppVersionCheck();

    useEffect(() => {
        // Change update period to 10p-2a
        const thisWindow = window as any;
        try {
            if(thisWindow.fully) {
                if (thisWindow.fully.getStringSetting("mdmSystemUpdatePolicy") != "2") {
                    thisWindow.fully.setStringSetting("mdmSystemUpdatePolicy","2");
                    console.log("Updated mdmSystemUpdatePolicy to 2 (10p-2am)");
                }

                thisWindow.fully.bind('thisWindow','logBroadcast("$action","$extras");');
            }
            else {
                console.log("Fully Kiosk not detected, skipping mdmSystemUpdatePolicy update");
            }
        } catch (error) {
            const errorObj = error as Error;

            console.error(`Error setting mdmSystemUpdatePolicy: ${errorObj.message}`, error);
        }
    }, []);

    return (
        <Router>
            <AppShellContent />
        </Router>
    );
};

export default function App() {
    return (
        <FaceSdkStoreContextItem.ProviderComponent>
            <MainStoreContextItem.ProviderComponent>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <AppImpl />
                </ThemeProvider>
            </MainStoreContextItem.ProviderComponent>
        </FaceSdkStoreContextItem.ProviderComponent>
    );
}
